import { useContext } from "react";
import { LoadingButton } from "@mui/lab";
import { Button, Grid, LinearProgress, Stack, Tooltip } from "@mui/material";
import { OrderContext } from "../contexts/OrderDataContext";
import ProofOfOrderPlacement from "./ProofOfOrderPlacement";
import Transaction from "./Transaction";
import Contacts from "./additionalContacts/Contacts";
import FormCard from "./FormCard";
import MatchedOrders from "./matchedOrders/MatchedOrders";
import Notes from "./Notes";
import OrderType from "./OrderType";
import PointOfContact from "./PointOfContact";
import PriorPolicy from "./PriorPolicy";
import Parties from "./sellerAndBuyer/Parties";
import TitleAgentAndUnderwriter from "./TitleAgentAndUnderwriter";
import OrderPageContainer from "./OrderPageContainer";
import OrderSuccess from "./OrderSuccess";
import ProfilePreview from "./ProfilePreview";
import PropertiesNew from "./PropertiesNew";
import BuyerSellerPrincipal from "./BuyerSellerPrincipal";
import OrderWarningsBanner from "./OrderWarningsBanner";
import OrderWarningsModal from "./OrderWarningsModal";
import { serializeOrderState } from "../contexts/serializeOrderState";
// ----------------------------------------------------------------------

const getMultiSiteStorageKey = (channelId: string) => `multi-site-order:${channelId}`;

export default function CreateOrder() {
  const {
    loading,
    orderNumber,
    identifier,
    submitOrder,
    sellers,
    setSellers,
    sellersAttorney,
    setSellersAttorney,
    buyers,
    setBuyers,
    buyersAttorney,
    setBuyersAttorney,
    transactionType,
    sellersParalegal,
    setSellersParalegal,
    buyersParalegal,
    setBuyersParalegal,
    notes,
    setNotes,
    properties,
    didFileUploadSucceed,
    currentBlacklistSearchRequestsCount,
    submitted
  } = useContext(OrderContext);

  const orderType = properties[0]?.propertyInfo?.propertyUse?.toUpperCase();

  const ctx = useContext(OrderContext);

  const placeAnotherSite = () => {
    const serializedState = serializeOrderState(ctx);
    const channelId = `order-${crypto.randomUUID()}`;
    const storageKey = getMultiSiteStorageKey(channelId);

    try {
      localStorage.setItem(storageKey, JSON.stringify(serializedState));
    } catch {}

    if (typeof BroadcastChannel !== "undefined") {
      const channel = new BroadcastChannel(channelId);

      channel.onmessage = (e) => {
        if (e.data.type === "ready") {
          channel.postMessage({
            serializedState
          });
        }

        if (e.data.type === "received") {
          channel.close();
          try {
            localStorage.removeItem(storageKey);
          } catch {}
        }
      };
    }

    window.open(`/order-entry?channel=${channelId}`, "_blank");
  };

  return (
    <OrderPageContainer title="Order Placement" activeStep={0}>
      <>
        <Grid container spacing={5}>
          <Grid item xs={12} md={8}>
            <Stack spacing={3}>
              {/* <FormCard title='4. Property'>
                <PropertiesNew />
              </FormCard> */}

              <FormCard title="1. Order Type">
                <OrderType />
              </FormCard>
              <FormCard title="2. Proof of Order Placement">
                <ProofOfOrderPlacement />
              </FormCard>
              <FormCard title="3a. Related Files">
                <PriorPolicy />
              </FormCard>

              <FormCard title="4. Property">
                <PropertiesNew />
              </FormCard>
              {(transactionType === "Purchase" || transactionType === "LastOwnerSearch") && (
                <FormCard title="5. Seller">
                  <Parties
                    isSeller
                    sameAsPropertyAddress
                    parties={sellers}
                    setParties={setSellers}
                    partyAttorney={sellersAttorney}
                    setPartyAttorney={setSellersAttorney}
                    partyParalegal={sellersParalegal}
                    setPartyParalegal={setSellersParalegal}
                  />
                </FormCard>
              )}
              <FormCard
                title={
                  transactionType === "Refinance" || transactionType === "LastOwnerSearch"
                    ? "5. Borrower"
                    : transactionType === "Purchase"
                    ? "6. Buyer"
                    : "5. Buyer"
                }
              >
                <Parties
                  isSeller={false}
                  sameAsPropertyAddress={true}
                  parties={buyers}
                  setParties={setBuyers}
                  partyAttorney={buyersAttorney}
                  setPartyAttorney={setBuyersAttorney}
                  partyParalegal={buyersParalegal}
                  setPartyParalegal={setBuyersParalegal}
                />
              </FormCard>
              {transactionType != "LastOwnerSearch" && (
                <FormCard title={(transactionType === "Purchase" ? "7. " : "6. ") + "Transaction"}>
                  <Transaction />
                </FormCard>
              )}
              <FormCard
                title={
                  (transactionType === "Purchase" ? "8. " : transactionType === "LastOwnerSearch" ? "6." : "7. ") +
                  "Title agent & Underwriter"
                }
              >
                <TitleAgentAndUnderwriter />
              </FormCard>
              <FormCard
                title={
                  (transactionType === "Purchase" ? "9. " : transactionType === "LastOwnerSearch" ? "7." : "8. ") +
                  (transactionType === "Refinance" ? "Borrower Principal" : "Buyer & Seller Principal")
                }
              >
                <BuyerSellerPrincipal />
              </FormCard>

              <FormCard
                title={
                  (transactionType === "Purchase" ? "10. " : transactionType === "LastOwnerSearch" ? "8." : "9. ") +
                  "Point of Contact"
                }
              >
                <PointOfContact />
              </FormCard>
              <FormCard
                title={
                  (transactionType === "Purchase" ? "11. " : transactionType === "LastOwnerSearch" ? "9." : "10. ") +
                  "Additional Contacts"
                }
              >
                <Contacts />
              </FormCard>
              <FormCard
                title={
                  (transactionType === "Purchase" ? "12. " : transactionType === "LastOwnerSearch" ? "10." : "11. ") +
                  "Notes"
                }
              >
                <Notes notes={notes} onChange={setNotes} />
              </FormCard>
            </Stack>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
            sx={{
              position: "sticky",
              top: 62,
              maxHeight: "90vh",
              overflowY: "scroll"
            }}
          >
            <MatchedOrders />
            <ProfilePreview />
          </Grid>
          <Grid item xs={12} md={8}>
            <OrderWarningsBanner />
            <OrderWarningsModal />
            <Stack alignItems="flex-end" marginBottom={2}>
              <Stack direction="row" spacing={1}>
                {!orderNumber && (
                  <Tooltip
                    title={
                      currentBlacklistSearchRequestsCount > 0 ? "Cannot submit while blacklist search is ongoing." : ""
                    }
                    arrow
                    disableHoverListener={!(currentBlacklistSearchRequestsCount > 0)}
                  >
                    <span>
                      <LoadingButton
                        onClick={submitOrder}
                        variant="contained"
                        disabled={loading || currentBlacklistSearchRequestsCount > 0}
                      >
                        {!loading ? "Submit Order" : "Submitting..."}
                      </LoadingButton>
                    </span>
                  </Tooltip>
                )}
                {submitted && !orderNumber && (
                  <Button variant="soft" onClick={placeAnotherSite}>
                    Place another site
                  </Button>
                )}
              </Stack>
            </Stack>
            {loading && <LinearProgress sx={{ marginTop: 4 }} />}
            {orderNumber && (
              <OrderSuccess
                orderNumber={orderNumber}
                identifier={identifier}
                orderType={orderType}
                didFileUploadSucceed={didFileUploadSucceed}
                placeAnotherSite={placeAnotherSite}
              />
            )}
          </Grid>
        </Grid>
      </>
    </OrderPageContainer>
  );
}
